import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { DateField, DurationField, SelectField, TextField } from '../../form';
import {
  useScenarioQuery,
  useSimulationTypesQuery,
} from '../../../network/queries';
import { Durations } from '../../../constants';
import {
  useCloneScenarioMutation,
  useUpdateScenarioMutation,
} from '../../../network/mutations';
import { createDuration, parseDuration } from '../../../utils/dateUtil';
import { Loader, Toolbar } from '../../common';

import HeaderCard from './HeaderCard';

const DEFAULT_VALUES = {
  name: '',
  description: '',
  runtime_input_start: dayjs(),
  runtime_input_duration: 0,
  runtime_input_duration_unit: Durations.DAYS,
  reporter_input_period: 0,
  reporter_input_period_unit: Durations.DAYS,
  simulation_type_id: '',
};

function SettingsStep({ editing, onNext }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const cloneMutation = useCloneScenarioMutation();
  const updateMutation = useUpdateScenarioMutation();
  const {
    data: updatedScenario,
    mutate,
    isPending: isMutationPending,
    isSuccess: isMutationSuccess,
  } = editing ? updateMutation : cloneMutation;

  const { scenarioId } = useParams();
  const { isLoading: isLoadingScenario, data: scenario } =
    useScenarioQuery(scenarioId);

  const { isLoading: isLoadingSimulationTypes, data: simulationTypes } =
    useSimulationTypesQuery();

  const { control, handleSubmit, reset } = useForm({
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    if (!editing && !isLoadingSimulationTypes && simulationTypes?.length) {
      reset({ ...DEFAULT_VALUES, simulation_type_id: simulationTypes[0].id });
    }
  }, [isLoadingSimulationTypes, simulationTypes]);

  useEffect(() => {
    if (editing && !isLoadingScenario && scenario) {
      const runtimeDuration = parseDuration(scenario.runtime_input.duration);
      const reporterPeriod = parseDuration(scenario.reporter_input.period);
      reset({
        name: scenario.name,
        description: scenario.description,
        runtime_input_start: dayjs(scenario.runtime_input_start),
        runtime_input_duration: runtimeDuration[0],
        runtime_input_duration_unit: runtimeDuration[1],
        reporter_input_period: reporterPeriod[0],
        reporter_input_period_unit: reporterPeriod[1],
        simulation_type_id: scenario.simulation_type_id,
      });
    }
  }, [isLoadingScenario, scenario]);

  const onSubmit = (data) => {
    mutate({
      scenarioId: editing
        ? scenario.id
        : simulationTypes.find((i) => i.id === data.simulation_type_id)
            .default_scenario_id,
      data: {
        name: data.name,
        description: data.description,
        runtime_input: {
          start: data.runtime_input_start.toISOString(),
          duration: createDuration(
            data.runtime_input_duration,
            data.runtime_input_duration_unit,
          ),
        },
        reporter_input: {
          period: createDuration(
            data.reporter_input_period,
            data.reporter_input_period_unit,
          ),
        },
      },
    });
  };

  useEffect(() => {
    // TODO: show mutation error
    if (
      !editing &&
      !isMutationPending &&
      isMutationSuccess &&
      updatedScenario
    ) {
      navigate(`../scenarios/${updatedScenario.id}/edit`);
    }
  }, [isMutationPending, isMutationSuccess, updatedScenario]);

  return isLoadingScenario || isLoadingSimulationTypes ? (
    <Loader />
  ) : (
    <Stack spacing={2} sx={{ flex: 1 }}>
      <HeaderCard title={t('pages.scenarios.configure.settings.title')}>
        <Typography>
          {t('pages.scenarios.configure.settings.subheader')}
        </Typography>
      </HeaderCard>
      <Card>
        <CardContent>
          <Box
            id="scenario-configuration-form"
            component="form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <Stack spacing={2}>
              <TextField
                label={t('pages.scenarios.configure.settings.form.name')}
                tooltip={t(
                  'pages.scenarios.configure.settings.form.nameTooltip',
                )}
                name="name"
                control={control}
                rules={{ required: t('common.validation.required') }}
              />
              <TextField
                label={t('pages.scenarios.configure.settings.form.description')}
                tooltip={t(
                  'pages.scenarios.configure.settings.form.descriptionTooltip',
                )}
                name="description"
                control={control}
              />
              <SelectField
                label={t(
                  'pages.scenarios.configure.settings.form.simulationType',
                )}
                tooltip={t(
                  'pages.scenarios.configure.settings.form.simulationTypeTooltip',
                )}
                name="simulation_type_id"
                control={control}
                rules={{ required: t('common.validation.required') }}
                disabled={editing}
                items={(isLoadingSimulationTypes ? [] : simulationTypes).map(
                  (i) => ({
                    key: i.id,
                    label: i.sim_type,
                    value: i.id,
                  }),
                )}
              />
              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={1}>
                    <Typography
                      variant="overline"
                      gutterBottom
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {t(
                        'pages.scenarios.configure.settings.form.runtimeInput',
                      )}
                    </Typography>
                    <DateField
                      label={t(
                        'pages.scenarios.configure.settings.form.runtimeInputStart',
                      )}
                      tooltip={t(
                        'pages.scenarios.configure.settings.form.runtimeInputStartTooltip',
                      )}
                      name="runtime_input_start"
                      control={control}
                      rules={{ required: t('common.validation.required') }}
                    />
                    <DurationField
                      label={t(
                        'pages.scenarios.configure.settings.form.runtimeInputDuration',
                      )}
                      tooltip={t(
                        'pages.scenarios.configure.settings.form.runtimeInputDurationTooltip',
                      )}
                      name="runtime_input_duration"
                      unitName="runtime_input_duration_unit"
                      control={control}
                      rules={{
                        required: t('common.validation.required'),
                        min: {
                          value: 1,
                          message: t('common.validation.min', { value: 1 }),
                        },
                      }}
                    />
                  </Stack>
                </CardContent>
              </Card>
              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={1}>
                    <Typography
                      variant="overline"
                      gutterBottom
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {t(
                        'pages.scenarios.configure.settings.form.reporterInput',
                      )}
                    </Typography>
                    <DurationField
                      label={t(
                        'pages.scenarios.configure.settings.form.reporterInputPeriod',
                      )}
                      tooltip={t(
                        'pages.scenarios.configure.settings.form.reporterInputPeriodTooltip',
                      )}
                      name="reporter_input_period"
                      unitName="reporter_input_period_unit"
                      control={control}
                      rules={{
                        required: t('common.validation.required'),
                        min: {
                          value: 1,
                          message: t('common.validation.min', { value: 1 }),
                        },
                      }}
                    />
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Box>
        </CardContent>
      </Card>
      <Toolbar>
        <Button
          disabled={isMutationPending}
          onClick={() => navigate('../scenarios')}
        >
          {t('common.back')}
        </Button>
        <Button onClick={onNext} disabled={!scenarioId || isMutationPending}>
          {t('common.next')}
        </Button>
        <Box sx={{ ml: 'auto' }}>
          <Button
            variant="contained"
            type="submit"
            form="scenario-configuration-form"
            startIcon={<FontAwesomeIcon icon="floppy-disk" />}
            loading={isMutationPending}
            loadingPosition="start"
          >
            {t('common.save')}
          </Button>
        </Box>
      </Toolbar>
    </Stack>
  );
}

export default SettingsStep;
